import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
} from '@mui/material';
import Logo from '../../components/common/Logo';
import { offerDetails } from '../../services/api';

interface OfferData {
  id: number;
  title: string;
  image: string;
  price: string;
}

interface TrackingFormData {
  email: string;
  trackingId: string;
}

interface FormErrors {
  email?: string;
  trackingId?: string;
  captcha?: string;
}

const OfferTracking: React.FC = () => {
  const { offerCode } = useParams<{ offerCode: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [offerData, setOfferData] = useState<OfferData | null>(null);
  const [offerLoading, setOfferLoading] = useState(true);
  const [formData, setFormData] = useState<TrackingFormData>({
    email: '',
    trackingId: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [isFromLink, setIsFromLink] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.trackingId.trim()) {
      newErrors.trackingId = 'Tracking ID is required';
    }

    if (!captchaToken) {
      newErrors.captcha = 'Please complete the captcha';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for the field being changed
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
    if (errors.captcha) {
      setErrors(prev => ({ ...prev, captcha: undefined }));
    }
  };

  const handleContinue = async () => {
    if (!validateForm()) {
      return;
    }

    setApiError(null);
    setLoading(true);
    try {
      const response = await offerDetails.trackOffer(formData.email, formData.trackingId, captchaToken || '');
      
      if (response.success) {
        // Navigate to buyer offer detail page on success with trackOffer data
        const targetOfferCode = offerCode || response.data.uniqueId || response.data.id;
        navigate(`/offer/${targetOfferCode}/buyer-offer-detail`, { 
          state: { 
            offerData,
            trackOfferData: response.data 
          } 
        });
      } else {
        setApiError(response.message || 'Failed to track offer');
        // Clear form fields and reset captcha on error
        setFormData({ email: '', trackingId: '' });
        setCaptchaToken(null);
        recaptchaRef.current?.reset();
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to track offer';
      setApiError(errorMessage);
      // Clear form fields and reset captcha on error
      setFormData({ email: '', trackingId: '' });
      setCaptchaToken(null);
      recaptchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchOfferDetails = async () => {
      if (!offerCode) {
        setOfferLoading(false);
        return;
      }

      let isBase64 = false;
      try {
        const decoded = atob(offerCode);
        if (decoded.includes('|')) {
          const parts = decoded.split('|');
          if (parts.length === 2 && parts[0].includes('@')) {
            setFormData({ email: parts[0], trackingId: parts[1] });
            setIsFromLink(true);
            isBase64 = true;
          }
        }
      } catch (e) {
        // Not base64
      }

      // Check if offerData is passed from previous page
      if (location.state?.offerData) {
        setOfferData({
          id: location.state.offerData.id,
          title: location.state.offerData.title,
          image: location.state.offerData.image,
          price: location.state.offerData.price,
        });
        setOfferLoading(false);
        return;
      }

      if (isBase64) {
        setOfferLoading(false);
        return;
      }

      try {
        const response = await offerDetails.getOfferByIdForGuestUser(offerCode);
        if (response.success) {
          setOfferData({
            id: response.data.id,
            title: response.data.title,
            image: response.data.image,
            price: response.data.price,
          });
        }
      } catch (err) {
        console.error('Error fetching offer details:', err);
      } finally {
        setOfferLoading(false);
      }
    };

    fetchOfferDetails();
  }, [offerCode, location.state]);

  const handleBack = () => {
    if (offerCode) {
      navigate(`/offer/${offerCode}`);
    } else {
      navigate('/');
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      {/* Logo at the top */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <Logo variant="full" size="small" />
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {offerLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {offerData && (
              <Box sx={{ mb: 4, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                  {offerData.image && (
                    <Card sx={{ width: 100, height: 100, flexShrink: 0 }}>
                      <CardMedia
                        component="img"
                        sx={{ height: 100, objectFit: 'cover' }}
                        image={offerData.image}
                        alt={offerData.title}
                      />
                    </Card>
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {offerData.title}
                    </Typography>
                    <Typography variant="h5" color="primary" fontWeight="bold">
                      ${offerData.price}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
              Offer Tracking
            </Typography>

            {!isFromLink && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Please enter following details to track and view more details about your purchase.
              </Alert>
            )}

            {apiError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {apiError}
              </Alert>
            )}

            <Box component="form" noValidate>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            error={!!errors.email}
            helperText={errors.email}
            required
            disabled={isFromLink}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Tracking ID"
            name="trackingId"
            value={formData.trackingId}
            onChange={handleInputChange}
            error={!!errors.trackingId}
            helperText={errors.trackingId}
            required
            disabled={isFromLink}
            sx={{ mb: 3 }}
          />

          <Box sx={{ mb: 3 }}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
              onChange={handleCaptchaChange}
            />
            {errors.captcha && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {errors.captcha}
              </Typography>
            )}
          </Box>

          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleContinue}
            disabled={loading}
            sx={{
              borderRadius: 2,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 'bold',
              mb: 2,
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Continue'}
          </Button>

          <Button
            variant="outlined"
            fullWidth
            size="large"
            onClick={handleBack}
            disabled={loading}
            sx={{
              borderRadius: 2,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 'bold',
            }}
          >
            Back
          </Button>
        </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default OfferTracking;
